import classNames from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { postsApi } from 'src/api/posts';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { CommentsSideProps } from 'src/pages/UserCircle/const';

import { Comment } from 'src/api/comments';
import { useCommentViewingEngagement } from 'src/utils/commentEngagement';
import { CommentItem } from './CommentItem/CommentItem';
import styles from './CommentsSide.module.scss';
import { PostCommentInput } from './PostCommentInput/PostCommentInput';

export const CommentsSide: FC<CommentsSideProps> = ({
  commentsData,
  postId,
  loading,
  isChallengePage,
  isFeed = false,
  commentsCount,
  setCommentsCount,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [children, setChildren] = useState<Comment[][]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const tribeId = comments.length > 0 ? comments[0].tribe_id : '';
  useCommentViewingEngagement({
    postId,
    tribeId,
    hasComments: comments.length > 0,
  });

  useEffect(() => {
    setComments(commentsData.result ?? []);
    setChildren(commentsData.children ?? []);
    setPage(commentsData.pagination.currentPage ?? 1);
    setTotalPages(commentsData.pagination.totalPages ?? 1);
  }, [commentsData]);

  useEffect(() => {
    const hasUnreadyVideoComment = comments.some(
      (c) => c.media_type === 'video' && c.media?.status !== 'READY'
    );

    if (!hasUnreadyVideoComment) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const res = await postsApi.getCommentsForPost(postId, 1);
        const newComments = res.result ?? [];

        const stillHasUnready = newComments.some(
          (c: any) => c.media_type === 'video' && c.media?.status !== 'READY'
        );

        setComments(newComments);
        setChildren(res.children ?? []);

        if (!stillHasUnready) {
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Error updating comments:', error);
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [comments, postId]);

  const [loadingMore, setLoadingMore] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastCommentRef = useCallback(
    (node: HTMLDivElement) => {
      if (loadingMore) {
        return;
      }
      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && page < totalPages) {
          loadMoreComments();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingMore, page, totalPages]
  );

  const loadMoreComments = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await postsApi.getCommentsForPost(postId, nextPage);
      setComments((prev) => [...prev, ...res.result]);
      setChildren((prev) => [...prev, ...res.children]);
      setPage(res.pagination.currentPage ?? 1);
      setTotalPages(res.pagination.totalPages ?? 1);
    } catch (err) {
      console.error('Failed to load more comments', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleNewComment = (newComment: Comment) => {
    if (setCommentsCount) {
      setCommentsCount((prev) => prev + 1);
    }

    setComments((prev) => [newComment, ...prev]);
    setChildren((prev) => [[/* optional replies */], ...prev]);
  };

  const handleDeleteComment = (id: string) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment._id === id ? { ...comment, is_deleted: true } : comment
      )
    );
  };

  const handleEditComment = (commentId: string, newText: string) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment._id === commentId ? { ...comment, text: newText } : comment
      )
    );
  };

  return (
    <div className={styles.commentsWrapper}>
      <div className={styles.header}>
        <span className={styles.totalComments}>
          Comments ({commentsCount ? commentsCount : comments.length})
        </span>
      </div>

      <div className={styles.scrollableComments}>
        <div
          className={classNames(styles.commentsList, {
            [styles.isChallengePage]: isChallengePage,
            [styles.isFeedPage]: isFeed,
          })}
        >
          {comments.map((comment: Comment, index: number) => {
            const isLast = index === comments.length - 1;
            return (
              <div key={comment._id} ref={isLast ? lastCommentRef : null}>
                <CommentItem
                  comment={comment}
                  replies={children[index] || []}
                  postId={postId}
                  onDelete={handleDeleteComment}
                  onEdit={handleEditComment}
                  setCommentsCount={setCommentsCount}
                />
              </div>
            );
          })}
          {(loadingMore || loading) && (
            <div style={{ padding: 12 }}>
              <MaterialCircularProgress height="100px" size={42} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.replyBlock}>
        <PostCommentInput postId={postId} onCommentPosted={handleNewComment} />
      </div>
    </div>
  );
};
